import { useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import type { ComentarioSolicitacao } from '../../../../models';
import { formatarDataHora } from './observacoesSolicitacao.definitions';

interface ComentarioItemProps {
  comentario: ComentarioSolicitacao;
  fotoUrl: string | null;
  /** Só o autor edita/exclui (mesma regra do firestore.rules). */
  ehAutor: boolean;
  salvando: boolean;
  onSalvarEdicao: (texto: string) => Promise<unknown>;
  onExcluir: () => void;
}

export default function ComentarioItem({
  comentario,
  fotoUrl,
  ehAutor,
  salvando,
  onSalvarEdicao,
  onExcluir,
}: ComentarioItemProps) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(comentario.texto);

  const handleSalvar = async () => {
    try {
      await onSalvarEdicao(texto);
      setEditando(false);
    } catch {
      /* toast no onError da mutation */
    }
  };

  return (
    <li className="flex gap-3 rounded-md border border-gray-100 bg-gray-50 p-3">
      <div className="shrink-0">
        <CollaboratorAvatar name={comentario.autor.name} photoUrl={fotoUrl} size="small" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-gray-500">
            <span className="font-medium text-gray-700">{comentario.autor.name}</span>
            {' · '}
            {formatarDataHora(comentario.criadoEm)}
            {comentario.editadoEm && (
              <span title={`Editado em ${formatarDataHora(comentario.editadoEm)}`}> · (editado)</span>
            )}
          </p>
          {ehAutor && !editando && (
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setTexto(comentario.texto);
                  setEditando(true);
                }}
                title="Editar"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button type="button" className="text-gray-400 hover:text-red-600" onClick={onExcluir} title="Excluir">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {editando ? (
          <div className="mt-2 space-y-2">
            <textarea
              className="input-primary w-full"
              rows={3}
              value={texto}
              onChange={(event) => setTexto(event.target.value)}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={handleSalvar}
                disabled={salvando || !texto.trim()}
              >
                Salvar
              </button>
              <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-700">{comentario.texto}</p>
        )}
      </div>
    </li>
  );
}
